/**
 * Which subdomain problem to tell the user about (CL-18308).
 *
 * CL-18308: an already-taken subdomain was shown as "Invalid subdomain
 * format." The cause was one condition doing two jobs. The template ANDed
 * every format rule together with the availability flag:
 *
 *   v-else-if="... $v.subdomainValue.required && ... && subdomainInputValid !== false"
 *   ...
 *   <span v-else>Invalid subdomain format.</span>
 *
 * so ANY failure of that compound condition produced a FORMAT message --
 * including a perfectly well-formed subdomain that simply was not free. The
 * two predicates are separate questions with separate answers, and this module
 * is where they are kept apart.
 *
 * It lives in a plain .js module rather than in each component for two
 * reasons. The mapping was duplicated across
 * passport_account/CreateCompany.vue and account/register.vue, and duplicated
 * message logic drifting apart is precisely how one screen ends up saying
 * something the other does not. And a .vue computed cannot be exercised
 * without a component harness, while this can be tested directly.
 *
 * Three causes, three messages, no overlap:
 *
 *   malformed  -> "Invalid subdomain format."
 *   taken      -> "Subdomain is already used."
 *   reserved   -> "This subdomain is reserved, please choose a different one."
 *
 * Reserved is a genuinely third case, not a flavour of taken. Telling a user
 * that another customer owns `api` when nobody owns it is the same class of
 * wrong message as the bug being fixed.
 */

/** Reason strings as sent by vp5-back's availability endpoint. */
export const SUBDOMAIN_REASON_TAKEN = "taken";
export const SUBDOMAIN_REASON_RESERVED = "reserved";

/**
 * The user-facing message per cause. Exported so a test asserts the exact
 * strings in one place, and so neither component can hold its own copy.
 */
export const SUBDOMAIN_MESSAGES = {
  format: "Invalid subdomain format.",
  taken: "Subdomain is already used.",
  reserved: "This subdomain is reserved, please choose a different one.",
};

/**
 * The single problem worth showing, or null when there is none.
 *
 * Order matters and is not arbitrary: format is decided locally and first,
 * because there is no point reporting an availability verdict for a value that
 * cannot be a subdomain at all -- and because the availability flag for such a
 * value is stale by definition (the screens do not probe a malformed value).
 *
 * @param {Object} state
 * @param {boolean} state.formatValid Every local format rule passes.
 * @param {?boolean} state.available Server verdict. `null` means not answered
 *   yet (or a failed probe), which is NOT a problem to report -- reporting it
 *   would flash an error at a value still being checked.
 * @param {?string} state.reason Why it is unavailable, when it is.
 * @returns {?string}
 */
export function subdomainIssue({ formatValid, available, reason }) {
  if (!formatValid) {
    return SUBDOMAIN_MESSAGES.format;
  }

  // Strictly `=== false`. `null` is "not answered yet", and treating it as
  // unavailable is how a message appears before any answer exists.
  if (available === false) {
    return reason === SUBDOMAIN_REASON_RESERVED
      ? SUBDOMAIN_MESSAGES.reserved
      : SUBDOMAIN_MESSAGES.taken;
  }

  return null;
}

/**
 * The unavailability reason carried by an availability response body.
 *
 * Falls back to `taken` when the response has no `reason`. That fallback is
 * what lets the client fix ship independently of the backend half (vp5-back
 * #3296): against a backend that does not send a reason yet, a taken subdomain
 * still gets the correct "already used" message instead of the format message
 * this ticket is about, and `reserved` simply starts working once the backend
 * lands. Guessing `taken` is right because it is both the overwhelmingly
 * common cause and the one the ticket reports.
 *
 * @param {?Object} body the `data` of the axios response, i.e. the envelope
 * @returns {?string} null when the subdomain IS available
 */
export function unavailableReasonFrom(body) {
  const envelope = body || {};

  if (envelope.data) {
    return null;
  }

  return envelope.reason || SUBDOMAIN_REASON_TAKEN;
}

export default { subdomainIssue, unavailableReasonFrom, SUBDOMAIN_MESSAGES };
